import { useMemo } from "react";

import type { Team } from "~/api/Api";
import { filterMenuItemsByFeatureFlags } from "~/components/sidebar//utils";
import type { MenuItem } from "~/components/sidebar/Menu";
import { useAuth } from "~/contexts/AuthProvider";
import { useBillingAccess } from "~/contexts/BillingAccessContext";
import { useTeam } from "~/contexts/TeamProvider";
import {
  FEATURE_FLAGS,
  useFeatureFlag,
  useFeatureFlagsConfig,
} from "~/featureFlags";
import labels from "~/helpers/labels";
import { getUserFullName } from "~/helpers/user.ts";
import { useEndpointAccess } from "~/hooks/useEndpointAccess";
import { useTeams } from "~/hooks/useTeams";
import { routes } from "~/router";
import { isWhiteLabel } from "~/utils/white-label";

export const CURRENT_USER_MENU_TYPE = "currentUser";
export const HELP_CENTER_DEFAULT_URL = "http://help.alliai.com/en/";

export const HELP_CENTER_MENU_TYPE = "helpCenter";
export const NOTIFICATIONS_MENU_TYPE = "notifications";

const MAIN_MENU: MenuItem[] = [
  {
    id: "dashboard",
    label: labels.DASHBOARD,
    iconName: "DashboardIcon",
    link: routes.home(),
    feature: FEATURE_FLAGS.SITES_DASHBOARD,
  },
  {
    id: "ai-visibility-engine",
    label: "AI Visibility Engine",
    iconName: "GlobeIcon",
    link: routes.visibilityEngine.domains(),
    feature: FEATURE_FLAGS.DOMAINS,
  },
  {
    id: "team-members",
    label: labels.TEAM_MEMBERS,
    iconName: "TeamIcon",
    link: routes.teamMembers(),
    feature: FEATURE_FLAGS.TEAM_MEMBERS,
  },
  {
    id: "api-keys",
    label: labels.API_KEYS,
    iconName: "APIKeysIcon",
    link: routes.apiKeys(),
    feature: FEATURE_FLAGS.API_KEYS,
  },
  {
    id: "billing",
    label: labels.BILLING,
    iconName: "BillingIcon",
    feature: FEATURE_FLAGS.BILLING,
    subMenu: [
      {
        label: "Current Plan",
        link: routes.billing.subscription(),
      },
      { label: "Invoices", link: routes.billing.invoices() },
    ],
  },
  {
    id: "services",
    label: labels.SERVICES,
    iconName: "ServicesIcon",
    feature: FEATURE_FLAGS.SERVICES,
    subMenu: [
      {
        label: "Foundation Links",
        link: routes.services.brandLinks(),
        feature: FEATURE_FLAGS.SERVICES_BRAND_LINKS,
      },
      {
        label: "Authority Links",
        link: routes.services.nicheLinks(),
        feature: FEATURE_FLAGS.SERVICES_NICHE_LINKS,
      },
      {
        label: "Concierge Installation",
        link: routes.services.conciergeInstallation(),
        feature: FEATURE_FLAGS.SERVICES_CONCIERGE,
      },
      {
        label: "LLM Citations",
        link: routes.services.llm_citations(),
        feature: FEATURE_FLAGS.SERVICES_LLM_CITATIONS,
      },
      {
        label: "Community Links",
        link: routes.services.communityLinks(),
        feature: FEATURE_FLAGS.SERVICES_COMMUNITY_MENTIONS,
      },
      {
        label: "Past Orders",
        link: routes.services.orders(),
        feature: FEATURE_FLAGS.SERVICES,
      },
    ],
  },
  {
    id: "settings",
    label: labels.SETTINGS,
    iconName: "SettingIcon",
    link: routes.settings.root(),
    feature: FEATURE_FLAGS.SETTINGS,
  },
];

function getHelpCenterUrl(params: {
  teamExternal?: boolean;
  whiteLabel: boolean;
  email?: string | null;
}) {
  const { teamExternal, whiteLabel, email } = params;

  if (teamExternal) return null;
  if (!whiteLabel) return HELP_CENTER_DEFAULT_URL;

  return email ? `mailto:${email}` : null;
}

type UserMenuItem = {
  label: string;
  link: string;
  type?: string;
};

function buildUserMenu(
  currentUserName: string,
  team: Team | null,
): UserMenuItem[] {
  const helpCenterUrl = getHelpCenterUrl({
    teamExternal: team?.external,
    whiteLabel: isWhiteLabel(),
    email: team?.support_email,
  });

  const result: UserMenuItem[] = [];

  if (helpCenterUrl) {
    result.push({
      label: labels.HELP_CENTER,
      link: helpCenterUrl,
      type: HELP_CENTER_MENU_TYPE,
    });
  }

  if (!team?.external) {
    result.push({
      label: currentUserName,
      link: "#",
      type: CURRENT_USER_MENU_TYPE,
    });
  }

  result.push({
    label: "Notifications",
    link: "#",
    type: NOTIFICATIONS_MENU_TYPE,
  });

  return result;
}

export function useSidebar() {
  const { user } = useAuth();
  const { team } = useTeam();
  const { data: teams = [] } = useTeams();
  const { isBillingAuthorized } = useBillingAccess();
  const flags = useFeatureFlagsConfig();

  // The backend decides who may open these sections (e.g. an Autopto agency
  // without a card may not): ask their endpoints, probing only enabled
  // features.
  const canOpenTeamMembers = useEndpointAccess(
    "memberships",
    useFeatureFlag(FEATURE_FLAGS.TEAM_MEMBERS),
  );
  const canOpenApiKeys = useEndpointAccess(
    "accessTokens",
    useFeatureFlag(FEATURE_FLAGS.API_KEYS),
  );
  const canOpenServices = useEndpointAccess(
    "orders",
    useFeatureFlag(FEATURE_FLAGS.SERVICES),
  );
  const canOpenInvoices = useEndpointAccess("invoices", isBillingAuthorized);

  const shouldShowVisibilityEngine = Boolean(
    team && team.subscription?.price?.product?.prerendering,
  );

  const currentUserName = getUserFullName(user);

  const menu = useMemo(() => {
    // Several items depend on the team (the AI Visibility Engine, the
    // sections probed below): show the menu once it's known, in one go,
    // rather than items popping in one by one.
    if (!team) return [];

    // Mirrors SubscriptionRequiredRoute: without a usable subscription (or
    // for the clients of an agency without a card) every other section
    // sends the user back to billing, so only billing is worth a link.
    const isAppOpen =
      Boolean(team.subscription?.healthy) &&
      !(user?.tenant_account && team.autopto_card_required);

    // first, filter based on business logic
    const preparedMenu = MAIN_MENU.filter((item) => {
      if (!isAppOpen && item.id !== "billing") return false;

      if (item.id === "ai-visibility-engine") {
        return shouldShowVisibilityEngine;
      }

      if (item.id === "billing") {
        return isBillingAuthorized;
      }

      if (item.id === "team-members") return canOpenTeamMembers === true;
      if (item.id === "api-keys") return canOpenApiKeys === true;
      if (item.id === "services") return canOpenServices === true;

      return true;
    }).map((item) =>
      item.id === "billing" && canOpenInvoices !== true
        ? {
            ...item,
            subMenu: item.subMenu?.filter(
              (sub) => sub.link !== routes.billing.invoices(),
            ),
          }
        : item,
    );

    // filter by feature flags
    return filterMenuItemsByFeatureFlags(preparedMenu, flags);
  }, [
    team,
    user?.tenant_account,
    flags,
    isBillingAuthorized,
    shouldShowVisibilityEngine,
    canOpenTeamMembers,
    canOpenApiKeys,
    canOpenServices,
    canOpenInvoices,
  ]);

  const userMenu = useMemo(() => {
    if (!currentUserName) return [];

    return buildUserMenu(currentUserName, team);
  }, [currentUserName, team]);

  const isSidebarEmpty = useMemo(() => {
    const hasTeamSelect = teams.length > 0;
    const hasMainMenu = menu.length > 0;
    const hasUserMenu = userMenu.length > 0;

    return !hasTeamSelect && !hasMainMenu && !hasUserMenu;
  }, [teams.length, menu.length, userMenu.length]);

  return {
    menu,
    userMenu,
    isSidebarEmpty,
  };
}

export type SidebarHookReturn = ReturnType<typeof useSidebar>;
